// Service API pour la gestion des commandes
import API_BASE_URL from '../config/api';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Authentication helpers
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// ==================== ORDER ROUTES ====================

// Get order details by ID
export const getOrderDetails = async (orderId) => {
  try {
    const data = await apiCall(`/orders/${orderId}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

// Get user orders
export const getUserOrders = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const data = await apiCall(`/orders/?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    console.error('Error fetching user orders:', error);
    throw error;
  }
};

// Create order
export const createOrder = async (orderData) => {
  try {
    const data = await apiCall('/orders/', {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    return data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

// Create guest order
export const createGuestOrder = async (orderData) => {
  try {
    const data = await apiCall('/orders/guest', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    return data;
  } catch (error) {
    console.error('Error creating guest order:', error);
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (orderId, status) => {
  try {
    const data = await apiCall(`/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });
    return data;
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// Get order by payment intent ID
export const getOrderByPaymentIntent = async (paymentIntentId) => {
  try {
    const data = await apiCall(`/orders/payment-intent/${paymentIntentId}`, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    console.error('Error fetching order by payment intent:', error);
    throw error;
  }
};

// ==================== UTILITY FUNCTIONS ====================

// Dérive un statut de paiement lisible pour l'UI à partir des champs réels de l'Order
// (`paid`, `payment_method`) : l'API ne renvoie pas de champ `payment_status` en tant que tel.
const derivePaymentStatus = (order) => {
  if (order.payment_status) return order.payment_status; // déjà fourni (ex: fallback localStorage)
  if (order.paid) return 'succeeded';
  if (order.payment_method === 'cash') return 'cash_on_delivery';
  return 'pending'; // paiement en ligne pas encore confirmé côté serveur (webhook à venir)
};

// Format order data for display
export const formatOrderForDisplay = (order) => {
  return {
    id: order.id,
    orderId: order.order_id || order.id,
    status: order.status,
    paymentStatus: derivePaymentStatus(order),
    amount: order.total_amount || order.amount || 0,
    items: order.items || [],
    deliveryAddress: order.delivery_address || order.address,
    deliveryInstructions: order.delivery_instructions || '',
    estimatedDelivery: order.estimated_delivery || '30-45 minutes',
    createdAt: order.created_at || order.created,
    updatedAt: order.updated_at || order.updated,
    customer: order.customer || null,
    restaurant: order.restaurant || null
  };
};

// Get order details with fallback
export const getOrderDetailsWithFallback = async (orderId) => {
  try {
    // Try to get from API first
    const orderDetails = await getOrderDetails(orderId);
    return formatOrderForDisplay(orderDetails);
  } catch (error) {
    console.warn('Could not fetch order from API, using fallback:', error);
    
    // Fallback: try to get from localStorage
    const lastOrder = localStorage.getItem('lastOrder');
    if (lastOrder) {
      try {
        const parsedOrder = JSON.parse(lastOrder);
        return formatOrderForDisplay(parsedOrder);
      } catch (parseError) {
        console.error('Error parsing localStorage order:', parseError);
      }
    }
    
    // Final fallback: create default order details
    return {
      id: orderId,
      orderId: orderId,
      status: 'confirmed',
      paymentStatus: 'succeeded',
      amount: 0,
      items: [],
      deliveryAddress: 'Adresse de livraison',
      deliveryInstructions: '',
      estimatedDelivery: '30-45 minutes',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      customer: null,
      restaurant: null
    };
  }
};

// Save order to localStorage
export const saveOrderToLocalStorage = (orderDetails) => {
  try {
    localStorage.setItem('lastOrder', JSON.stringify(orderDetails));
    console.log('✅ Order saved to localStorage');
  } catch (error) {
    console.error('❌ Error saving order to localStorage:', error);
  }
};

// Clear order from localStorage
export const clearOrderFromLocalStorage = () => {
  try {
    localStorage.removeItem('lastOrder');
    console.log('✅ Order cleared from localStorage');
  } catch (error) {
    console.error('❌ Error clearing order from localStorage:', error);
  }
};

export default {
  getOrderDetails,
  getUserOrders,
  createOrder,
  createGuestOrder,
  updateOrderStatus,
  getOrderByPaymentIntent,
  formatOrderForDisplay,
  getOrderDetailsWithFallback,
  saveOrderToLocalStorage,
  clearOrderFromLocalStorage
};
